import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { OwnerUser, EmployeeUser, CustomerUser, UserType } from '@/services/auth';

/** @deprecated use OwnerUser from @/services/auth */
export type User = OwnerUser;

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  userType: UserType | null;
  /** Populated when userType === 'owner' */
  user: OwnerUser | null;
  /** Populated when userType === 'employee' */
  employee: EmployeeUser | null;
  /** Populated when userType === 'customer' */
  customer: CustomerUser | null;
  isLoggedIn: boolean;
  isPasswordTemporary: boolean;

  signin: (
    accessToken: string,
    refreshToken: string,
    userType: UserType,
    owner: OwnerUser | null,
    employee: EmployeeUser | null,
    isPasswordTemporary: boolean,
    customer?: CustomerUser | null
  ) => void;
  signout: () => void;
  updateUser: (data: Partial<OwnerUser>) => void;
  updateEmployee: (data: Partial<EmployeeUser>) => void;
  updateCustomer: (data: Partial<CustomerUser>) => void;
  setPasswordChanged: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      userType: null,
      user: null,
      employee: null,
      customer: null,
      isLoggedIn: false,
      isPasswordTemporary: false,

      signin: (
        accessToken,
        refreshToken,
        userType,
        owner,
        employee,
        isPasswordTemporary,
        customer = null
      ) =>
        set({
          accessToken,
          refreshToken,
          userType,
          user: owner,
          employee,
          customer,
          isLoggedIn: true,
          isPasswordTemporary,
        }),

      signout: () =>
        set({
          accessToken: null,
          refreshToken: null,
          userType: null,
          user: null,
          employee: null,
          customer: null,
          isLoggedIn: false,
          isPasswordTemporary: false,
        }),

      updateUser: (data) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...data } : null,
        })),

      updateEmployee: (data) =>
        set((state) => ({
          employee: state.employee ? { ...state.employee, ...data } : null,
        })),

      updateCustomer: (data) =>
        set((state) => ({
          customer: state.customer ? { ...state.customer, ...data } : null,
        })),

      setPasswordChanged: () => set({ isPasswordTemporary: false }),
    }),
    {
      name: 'sloxt-auth',
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          try {
            const payload = JSON.parse(atob(state.accessToken.split('.')[1]));
            if (payload.exp * 1000 < Date.now()) {
              state.signout();
            }
          } catch {
            state.signout();
          }
        }
      },
    }
  )
);
